import React, { useEffect, useState } from "react";
import {
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Autocomplete,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Snackbar,
  Alert,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StyleIcon from "@mui/icons-material/Style";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { usePawnEditAccess } from "./pawnEditAccess";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

/* ─── shared field sx ─────────────────────────────────────────── */
const tfSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontSize: "0.875rem",
    bgcolor: "#fafbfc",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a5b4fc" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2563EB",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#2563EB" },
};

/* ─── column definitions ─────────────────────────────────────── */
const COLS = [
  { key: "scheme_name", label: "Scheme", numeric: false },
  { key: "interest", label: "Int. %", numeric: true },
  { key: "interest_2", label: "Int. 2 %", numeric: true },
  { key: "upto_month_1", label: "≤ 1M", numeric: true },
  { key: "upto_month_1_3", label: "1–3M", numeric: true },
  { key: "upto_month_3_12", label: "3–12M", numeric: true },
  { key: "after_12", label: "> 12M", numeric: true },
];

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);

/* ─── number field fields ────────────────────────────────────── */
// Base rates: always editable. Interest is the official rate; Interest 2 is the
// rate applied to unofficial-mode loans (see resolveSchemeForMode in
// utils/pawnInterestCalculator).
const BASE_FIELDS = ["interest", "interest_2"];
// Month-band rates: only editable when "Month-wise interest" is on. A blank/0
// band means "use the scheme's base rate", so switching the checkbox off simply
// clears them all.
const TIER_FIELDS = [
  "upto_month_1",
  "upto_month_1_3",
  "upto_month_3_12",
  "after_12",
];
const NUM_FIELDS = [...BASE_FIELDS, ...TIER_FIELDS];

const FIELD_LABELS = {
  interest: "Interest (%)",
  interest_2: "Interest 2 (%)",
  upto_month_1: "≤ 1 Month",
  upto_month_1_3: "1–3 Months",
  upto_month_3_12: "3–12 Months",
  after_12: "After 12M",
};

const TIER_INFO = {
  title: "Different interest for different month",
  description:
    "Turn this on to charge a separate interest rate for each month band of the loan — up to 1 month, 1 to 3 months, 3 to 12 months, and after 12 months. Every receipt is then charged the rate of the band its month falls in, counted from the loan date. Leave it off to keep the four band fields blank and locked: the scheme then charges a single rate for the whole loan — Interest (%) in official mode, Interest 2 (%) in unofficial mode.",
};

// A saved scheme is "month-wise" when it actually carries at least one band
// rate — no extra column needed, because a blank/0 band is exactly what the
// interest engine treats as "no band, use the base rate".
const hasTierRates = (scheme) =>
  TIER_FIELDS.some((f) => parseFloat(scheme?.[f]) > 0);

/**
 * `embedded` — rendered as the body of the Interest Slab section, which already
 * supplies the card, the heading and the icon. So the component drops its own
 * chrome and just flexes into the space it was handed. Standalone it keeps the
 * card it always had.
 */
const PawnSchemeManagement = ({
  storeId,
  currentStatus,
  embedded = false,
  // Sent with every write: the scheme endpoints refuse a Store Staff user.
  userId = null,
}) => {
  // Store Staff see the schemes but get no add / edit / delete controls.
  const canEdit = usePawnEditAccess();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [formData, setFormData] = useState({
    scheme_name: "",
    interest: "",
    interest_2: "",
    upto_month_1: "",
    upto_month_1_3: "",
    upto_month_3_12: "",
    after_12: "",
  });
  // "Different interest for different month" — unlocks the four month-band
  // fields. Not a stored column: it is derived from the saved band rates.
  const [tieredInterest, setTieredInterest] = useState(false);
  const [tierInfoOpen, setTierInfoOpen] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [interestError, setInterestError] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchSchemes = async () => {
    if (storeId == null) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/schemes/${storeId}`,
      );
      if (res.data.success) setSchemes(res.data.data);
    } catch (e) {
      console.error(e);
      showSnackbar("Failed to load schemes", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeId != null) fetchSchemes();
  }, [storeId, currentStatus]);

  const openDialog = (scheme = null) => {
    setNameError(false);
    setInterestError(false);
    if (scheme) {
      setEditingScheme(scheme);
      setFormData({ ...scheme });
      setTieredInterest(hasTierRates(scheme));
    } else {
      setEditingScheme(null);
      setTieredInterest(false);
      setFormData({
        scheme_name: "",
        interest: "",
        interest_2: "",
        upto_month_1: "",
        upto_month_1_3: "",
        upto_month_3_12: "",
        after_12: "",
      });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingScheme(null);
    setNameError(false);
    setInterestError(false);
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    if (NUM_FIELDS.includes(field)) {
      if (value === "") {
        setFormData((p) => ({ ...p, [field]: "" }));
        return;
      }
      const n = parseFloat(value);
      if (!isNaN(n) && n >= 0) setFormData((p) => ({ ...p, [field]: n }));
      if (field === "interest" && !isNaN(n) && n > 0) setInterestError(false);
    } else {
      setFormData((p) => ({ ...p, [field]: value }));
    }
    if (field === "scheme_name" && value.trim()) setNameError(false);
  };

  // Switching month-wise interest OFF wipes the band rates, so the scheme falls
  // back to its single base rate — the fields are then blank AND locked, which
  // is exactly what gets saved (nulls).
  const handleTieredToggle = (checked) => {
    setTieredInterest(checked);
    if (!checked)
      setFormData((p) => ({
        ...p,
        ...Object.fromEntries(TIER_FIELDS.map((f) => [f, ""])),
      }));
  };

  const handleSave = async () => {
    if (!formData.scheme_name.trim()) {
      setNameError(true);
      showSnackbar("Scheme name is required", "error");
      return;
    }
    const interestVal = parseFloat(formData.interest);
    if (isNaN(interestVal) || interestVal <= 0) {
      setInterestError(true);
      showSnackbar("Interest (%) must be greater than 0", "error");
      return;
    }
    setSaveLoading(true);
    try {
      const payload = {
        scheme_name: formData.scheme_name.trim(),
        user_id: userId,
        ...Object.fromEntries(
          NUM_FIELDS.map((f) => {
            // Month-wise interest off ⇒ the band rates are cleared on the row
            // too, so the scheme charges its single base rate everywhere.
            if (TIER_FIELDS.includes(f) && !tieredInterest) return [f, null];
            const n = parseFloat(formData[f]);
            return [f, Number.isFinite(n) ? n : null];
          }),
        ),
      };
      if (editingScheme) {
        await axios.put(
          `${API_BASE_URL}/api/pawn/schemes/${storeId}/${editingScheme.id}`,
          payload,
        );
        showSnackbar("Scheme updated", "success");
      } else {
        await axios.post(
          `${API_BASE_URL}/api/pawn/schemes/${storeId}`,
          payload,
        );
        showSnackbar("Scheme created", "success");
      }
      await fetchSchemes();
      closeDialog();
    } catch (e) {
      console.error(e);
      showSnackbar("Failed to save scheme", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaveLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/pawn/schemes/${storeId}/${toDelete}`,
        {
          active: false,
          user_id: userId,
        },
      );
      showSnackbar("Scheme deactivated", "success");
      await fetchSchemes();
    } catch (e) {
      showSnackbar("Failed to deactivate scheme", "error");
    } finally {
      setSaveLoading(false);
      setDeleteOpen(false);
      setToDelete(null);
    }
  };

  const filteredSchemes = search.trim()
    ? schemes.filter((s) =>
        (s.scheme_name || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      )
    : schemes;

  return (
    <>
      <Box
        sx={
          embedded
            ? {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }
            : {
                bgcolor: "#ffffff",
                borderRadius: "16px",
                border: "1px solid #e8eaf0",
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }
        }
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            p: 1,
            flexShrink: 0,
          }}
        >
          {/* Heading — the Interest Slab section supplies it when embedded */}
          {!embedded && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: "#f3e8ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9333ea",
                  "& svg": { fontSize: 17 },
                }}
              >
                <StyleIcon />
              </Box>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: "0.88rem",
                }}
              >
                Scheme Master
              </Typography>
            </Box>
          )}

          {/* Search + Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", md: "space-between" },
              gap: 1.5,
              flex: { md: 1 },
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: { md: "center" },
              }}
            >
              <Autocomplete
                freeSolo
                size="small"
                options={schemes.map((s) => s.scheme_name).filter(Boolean)}
                inputValue={search}
                onInputChange={(_, val) => setSearch(val)}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", md: 360 },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search schemes"
                    sx={tfSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Store Staff view the schemes; adding or changing one is
                for the Store Head / HOD (pawnEditAccess.js). */}
            {canEdit && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => openDialog()}
                sx={{
                  bgcolor: "#2563EB",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  px: 1.8,
                  py: 0.6,
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    bgcolor: "#4f46e5",
                    boxShadow: "none",
                  },
                }}
              >
                Scheme
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Table ── */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#2563EB" }} />
            </Box>
          ) : (
            <>
              {/* Flex-sized, not `maxHeight: calc(100vh - 320px)`: this now
                  sits inside a fixed-height settings quadrant whose leftover
                  space depends on the basis toggle above it, so a measured
                  constant would only ever line up on one screen. */}
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {COLS.map((col) => (
                        <TableCell
                          key={col.key}
                          align={col.numeric ? "center" : "left"}
                          sx={{
                            bgcolor: "#f8f9fc",
                            color: "#64748b",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: "0.4px",
                            textTransform: "uppercase",
                            borderBottom: "1px solid #e8eaf0",
                            py: 1.2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                      {canEdit && (
                        <TableCell
                          sx={{
                            bgcolor: "#f8f9fc",
                            color: "#64748b",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: "0.4px",
                            textTransform: "uppercase",
                            borderBottom: "1px solid #e8eaf0",
                            py: 1.2,
                          }}
                        >
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSchemes.map((scheme, idx) => (
                      <TableRow
                        key={scheme.id}
                        sx={{
                          bgcolor: idx % 2 === 0 ? "#fff" : "#fafbfc",
                          "&:hover": { bgcolor: "#f5f3ff" },
                          transition: "background-color 0.15s",
                        }}
                      >
                        {COLS.map((col) => (
                          <TableCell
                            key={col.key}
                            align={col.numeric ? "center" : "left"}
                            sx={{
                              fontSize: "0.82rem",
                              color: col.numeric ? "#475569" : "#1a1a2e",
                              fontWeight: col.numeric ? 400 : 500,
                              borderBottom: "1px solid #f0f2f5",
                              py: 1,
                            }}
                          >
                            {fmt(scheme[col.key])}
                          </TableCell>
                        ))}
                        {canEdit && (
                          <TableCell
                            sx={{ borderBottom: "1px solid #f0f2f5", py: 0.8 }}
                          >
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Edit" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openDialog(scheme)}
                                  sx={{
                                    color: "#2563EB",
                                    bgcolor: "#eef2ff",
                                    borderRadius: "7px",
                                    width: 28,
                                    height: 28,
                                    "&:hover": { bgcolor: "#e0e7ff" },
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setToDelete(scheme.id);
                                    setDeleteOpen(true);
                                  }}
                                  sx={{
                                    color: "#ef4444",
                                    bgcolor: "#fef2f2",
                                    borderRadius: "7px",
                                    width: 28,
                                    height: 28,
                                    "&:hover": { bgcolor: "#fee2e2" },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {filteredSchemes.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={COLS.length + (canEdit ? 1 : 0)}
                          align="center"
                          sx={{ py: 5, color: "#94a3b8", fontSize: "0.85rem" }}
                        >
                          {search.trim()
                            ? `No schemes match "${search.trim()}".`
                            : 'No schemes yet — click "Add Scheme" to create one.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Box>

      {/* ── Create / Edit Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : "16px", overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            fontWeight: 700,
            fontSize: "1rem",
            bgcolor: "#fafbfc",
            borderBottom: "1px solid #f0f2f5",
            py: 1.8,
            px: 3,
            color: "#1a1a2e",
          }}
        >
          <Box component="span">
            {editingScheme ? "Edit Scheme" : "New Scheme"}
          </Box>

          {/* Month-wise interest toggle — unlocks the four band fields */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={tieredInterest}
                  onChange={(e) => handleTieredToggle(e.target.checked)}
                  sx={{
                    p: 0.5,
                    color: "#cbd5e1",
                    "&.Mui-checked": { color: "#2563EB" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: tieredInterest ? "#2563EB" : "#64748b",
                  }}
                >
                  Month-wise interest
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Tooltip title="Know more" arrow>
              <IconButton
                size="small"
                onClick={() => setTierInfoOpen(true)}
                sx={{
                  p: "2px",
                  color: "#94a3b8",
                  "&:hover": { color: "#2563EB", bgcolor: "#2563EB12" },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: "24px !important",
            pb: 2,
            px: 3,
          }}
        >
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Scheme Name"
                value={formData.scheme_name}
                onChange={handleFieldChange("scheme_name")}
                required
                error={nameError}
                helperText={nameError ? "Required" : ""}
                sx={tfSx}
              />
            </Grid>
            {NUM_FIELDS.map((field) => {
              // Band fields are blank and read-only until month-wise interest is
              // switched on.
              const locked = TIER_FIELDS.includes(field) && !tieredInterest;
              return (
                <Grid item md={3} xs={6} key={field}>
                  <TextField
                    fullWidth
                    size="small"
                    label={FIELD_LABELS[field]}
                    type="number"
                    value={locked ? "" : formData[field]}
                    onChange={handleFieldChange(field)}
                    disabled={locked}
                    required={field === "interest"}
                    error={field === "interest" && interestError}
                    helperText={
                      field === "interest" && interestError
                        ? "Must be greater than 0"
                        : ""
                    }
                    inputProps={{ min: 0, step: "any" }}
                    sx={tfSx}
                  />
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #f0f2f5",
            bgcolor: "#fafbfc",
          }}
        >
          <Button
            onClick={closeDialog}
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saveLoading}
            sx={{
              bgcolor: "#2563EB",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "none",
              "&:hover": { bgcolor: "#4f46e5", boxShadow: "none" },
            }}
          >
            {saveLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : editingScheme ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── "Know more": month-wise interest ── */}
      <Dialog
        open={tierInfoOpen}
        onClose={() => setTierInfoOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            fontSize: "1rem",
            color: "#1a1a2e",
            pb: 1,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20, color: "#2563EB" }} />
          {TIER_INFO.title}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "#374151", lineHeight: 1.65, fontSize: "0.88rem" }}
          >
            {TIER_INFO.description}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTierInfoOpen(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#2563EB",
              boxShadow: "none",
              "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "#1a1a2e",
            pt: 2.5,
            px: 3,
          }}
        >
          Delete Scheme?
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            This action cannot be undone. The scheme will be permanently
            removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={saveLoading}
            sx={{
              bgcolor: "#ef4444",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "none",
              "&:hover": { bgcolor: "#dc2626", boxShadow: "none" },
            }}
          >
            {saveLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PawnSchemeManagement;
